import { useEffect, useRef, useState, forwardRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/api';
import { Calendar, X } from 'lucide-react';
import BackButton from '../../components/BackButton';

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const addressInputRef = useRef(null);
  const isAutocompleteInitialized = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    emails: [''],
    phone: '',
    address: '',
    dob: '',
    ndisNumber: '',
    about: '',
    allergies: '',
    medications: '',
    emergencyContact: '',
    planType: 'self',
    goals: '',
    otherInfo: '',
    latitude: '',
    longitude: '',
    placeId: '',
    profilePicture: '',
    documents: [] // ✅ new
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [removedDocuments, setRemovedDocuments] = useState([]); // ✅ new
  const token = localStorage.getItem('token');
  const [deletingDoc, setDeletingDoc] = useState(false);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/clients/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let data = res.data;

        if (typeof data.emails === 'string') {
          try {
            data.emails = JSON.parse(data.emails);
          } catch {
            data.emails = [];
          }
        }

        if (typeof data.documents === 'string') {
          try {
            data.documents = JSON.parse(data.documents);
          } catch {
            data.documents = [];
          }
        }

        setFormData(data);
      } catch {
        toast.error('Failed to fetch client');
      }
    };
    if (token) fetchClient();
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailChange = (idx, value) => {
    const updated = [...formData.emails];
    updated[idx] = value;
    setFormData(prev => ({ ...prev, emails: updated }));
  };

  const addEmailField = () => {
    setFormData(prev => ({ ...prev, emails: [...prev.emails, ''] }));
  };

  const removeEmailField = (idx) => {
    const updated = [...formData.emails];
    updated.splice(idx, 1);
    setFormData(prev => ({ ...prev, emails: updated }));
  };

  const handleDateInputChange = (e) => {
    const input = e.target.value.replace(/-/g, '/');
    setFormData(prev => ({ ...prev, dob: input }));
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    const formatted = dayjs(date).format('DD/MM/YY');
    setFormData(prev => ({ ...prev, dob: formatted }));
  };

  const convertDisplayDOBtoDate = (dobStr) => {
    const parsed = dayjs(dobStr, 'DD/MM/YY');
    return parsed.isValid() ? parsed.toDate() : null;
  };

  const dobDate = useMemo(() => convertDisplayDOBtoDate(formData.dob), [formData.dob]);

  const initializeGoogleAutocomplete = () => {
    if (!window.google || isAutocompleteInitialized.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: ['au','nzl'] }
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      setFormData(prev => ({
        ...prev,
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        placeId: place.place_id
      }));
    });
    isAutocompleteInitialized.current = true;
  };

  const CalendarButton = forwardRef(({ onClick }, ref) => (
    <Calendar ref={ref} onClick={onClick}
      className="w-6 h-6 text-gray-400 hover:text-gray-200 cursor-pointer mt-3" />
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    formData.emails.forEach(email => data.append('emails', email));

    for (let key of [
      'name','phone','address','dob','ndisNumber','about',
      'allergies','medications','emergencyContact','planType',
      'goals','otherInfo','latitude','longitude','placeId'
    ]) {
      data.append(key, formData[key]);
    }

    if (profilePictureFile) data.append('profilePicture', profilePictureFile);
    if (documentFiles.length > 0) {
      documentFiles.forEach(file => data.append('documents', file));
    }
    if (removedDocuments.length > 0) {
      removedDocuments.forEach(doc => data.append('removedDocuments[]', doc));
    }

    try {
      await axios.put(`${API_BASE_URL}/api/clients/${id}`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Client updated successfully!');
      setTimeout(() => navigate('/clients'), 1500);
    } catch {
      toast.error('Failed to update client');
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
  
    try {
      setDeletingDoc(true);
  
      await axios.delete(`${API_BASE_URL}/api/clients/${id}/document`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { document: doc }
      });
  
      // Update UI after deletion
      setFormData(prev => ({
        ...prev,
        documents: (prev.documents || []).filter(d => d !== doc)
      }));
  
      // Add to removedDocuments for consistency (not strictly needed here anymore)
      setRemovedDocuments(prev => [...prev, doc]);
  
      toast.success('Document deleted successfully.');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete document.');
    } finally {
      setDeletingDoc(false);
    }
  };
  
  
  return (
    <div className="p-6 bg-[#0E0A1F] min-h-screen text-white">
      <BackButton/>
      <div className="max-w-2xl mx-auto bg-[#1C1235] p-6 rounded-xl shadow">
        <h2 className="text-2xl mb-4 font-bold">Edit Client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Full Name */}
          <div>
            <label className="block mb-1">Full Name</label>
            <input type="text" name="name" value={formData.name}
              onChange={handleChange} placeholder="Full Name"
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          {/* Emails */}
          <div>
            <label className="block mb-1">Email Addresses</label>
            {formData.emails.map((email, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input type="email" value={email}
                  onChange={e => handleEmailChange(idx, e.target.value)}
                  placeholder="Email address"
                  className="flex-1 p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
                {formData.emails.length > 1 && (
                  <button type="button" onClick={() => removeEmailField(idx)}
                    className="text-red-400 hover:text-red-600 text-xl leading-none">
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addEmailField}
              className="text-xs text-purple-400 underline">
              + Add another email
            </button>
          </div>

          {/* Phone and Other Fields */}
          {[{ label: 'Phone Number', name: 'phone' },
            { label: 'NDIS Number', name: 'ndisNumber' },
            { label: 'About the Client', name: 'about' },
            { label: 'Allergies', name: 'allergies' },
            { label: 'Medications', name: 'medications' },
            { label: 'Emergency Contact', name: 'emergencyContact' },
            { label: 'Goals', name: 'goals' },
            { label: 'Other Important Info', name: 'otherInfo' }].map(({ label, name }) => (
            <div key={name}>
              <label className="block mb-1">{label}</label>
              <input type="text" name={name} value={formData[name]} onChange={handleChange}
                placeholder={label} className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
            </div>
          ))}

          {/* Address */}
          <div>
            <label className="block mb-1">Address (Google Maps)</label>
            <input type="text" name="address" value={formData.address}
              onChange={handleChange} ref={addressInputRef}
              onFocus={initializeGoogleAutocomplete}
              placeholder="Start typing address..."
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          {/* DOB */}
          <div>
            <label className="block mb-1">Date of Birth (DD/MM/YY)</label>
            <div className="flex gap-2">
              <input
                type="text"
                name="dob"
                value={formData.dob}
                onChange={handleDateInputChange}
                placeholder="dd/MM/YY"
                className="flex-1 p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
              />
              <DatePicker
                selected={dobDate}
                onChange={handleCalendarSelect}
                dateFormat="dd/MM/yy"
                customInput={<CalendarButton />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>

          {/* Plan Type */}
          <div>
            <label className="block mb-1">Plan Type</label>
            <select name="planType" value={formData.planType}
              onChange={handleChange}
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]">
              <option value="self">Self-managed</option>
              <option value="plan">Plan-managed</option>
            </select>
          </div>

          {/* Existing Documents Display with Delete */}
          {formData.documents?.length > 0 && (
            <div className="mb-2">
              <label className="block mb-1">Existing Documents</label>
              <ul className="text-sm text-gray-300 list-disc list-inside space-y-1">
                {formData.documents
                  .filter(doc => !removedDocuments.includes(doc))
                  .map((doc, idx) => {
                    const fileName = doc.split('-')[1] || doc;
                    return (
                      <li key={idx} className="flex justify-between items-center">
                        <a
                          href={`${API_BASE_URL}${doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-purple-300 underline"
                        >
                          {fileName}
                        </a>
                        <button
  type="button"
  onClick={() => handleDeleteDocument(doc)}
  disabled={deletingDoc}
  className="text-red-400 hover:text-red-600 text-xs ml-4 disabled:opacity-50"
>
  {deletingDoc ? 'Deleting...' : 'Delete'}
</button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {/* Upload New Documents */}
          <div>
            <label className="block mb-1">Upload Documents (.pdf, .doc)</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              multiple
              onChange={e => setDocumentFiles([...e.target.files])}
              className="w-full p-2 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]"
            />
            {documentFiles.length > 0 && (
              <ul className="mt-2 text-sm text-gray-300 list-disc list-inside">
                {Array.from(documentFiles).map((file, idx) => (
                  <li key={idx}>{file.name}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Profile Picture Preview */}
          {formData.profilePicture && (
            <div>
              <label className="block mb-1">Current Profile Picture</label>
              <img src={`${API_BASE_URL}${formData.profilePicture}`} alt="Profile"
                className="w-20 h-20 rounded-full object-cover border border-gray-500" />
            </div>
          )}

          {/* Upload New Profile Picture */}
          <div>
            <label className="block mb-1">Change Profile Picture</label>
            <input type="file" accept="image/*"
              onChange={e => setProfilePictureFile(e.target.files[0])}
              className="w-full p-2 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          <button type="submit"
            className="w-full py-3 bg-[#8F4EFF] hover:bg-purple-700 rounded font-semibold">
            Update Client
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditClient;
